/**
 * Edit dataset modal — M6 §4 `PATCH /datasets/{id}`.
 *
 * The same field grammar as the wizard's step 2 and step 3 (title, domain, source, licence,
 * cadence, abstract, lineage, contact, access class, public visibility), over a dataset that
 * already exists. The wizard's `MetadataForm` is not reused as a component: it takes a
 * `WizardState` — a file, an upload row, detected facts — none of which this dialog has. The
 * fields themselves now live in `DatasetMetadataForm`, which the rail's Update Metadata
 * panel renders inline (client, 2026-09-18); this file keeps the dialog around them — the
 * focus trap, the two-stage fill, the PATCH — and passes the `mgr-edit` id prefix, so every
 * field's id and test id is exactly what it was.
 *
 * **The row paints, the record fills in.** The list carries title, abstract, domain, source,
 * classification and visibility; licence, cadence, lineage and contact live only on
 * `GET /datasets/{id}`. So the dialog opens instantly on the row and the rest of the form
 * arrives a moment later — but only into fields the reader has not already touched
 * (`touched`), because overwriting someone's half-typed licence with the server's old one is
 * the bug that pattern usually ships with.
 *
 * Focus, Tab-trapping and Escape are `ReviewModal`'s, which are `catalogue/DatasetRecord`'s.
 */
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  ApiError,
  fetchConfig,
  fetchDataset,
  patchDataset,
  queryKeys,
  type DatasetPatch,
  type ManagedDatasetRow,
} from '../../lib/api';
import { foldRecord, patchBody } from './manager';
import { Icon } from '../../lib/icons';
import { toast } from '../../lib/toast';
import { DatasetMetadataForm } from './DatasetMetadataForm';

/** Everything inside the dialog a Tab can reach, in document order (the record's list). */
const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type EditForm = DatasetPatch;

const fromRow = (row: ManagedDatasetRow): EditForm => ({
  title: row.title,
  abstract: row.abstract,
  domain: row.domain,
  source_org: row.source_org,
  licence: null,
  cadence: null,
  lineage: null,
  contact: null,
  classification: row.classification,
  website_visible: row.website_visible,
});

export interface EditDatasetModalProps {
  row: ManagedDatasetRow;
  onClose: () => void;
  /** Called after a successful PATCH, before the dialog closes — the tab refetches. */
  onSaved?: () => void;
}

export function EditDatasetModal({ row, onClose, onSaved }: EditDatasetModalProps) {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const [form, setForm] = useState<EditForm>(() => fromRow(row));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  const config = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const domains = config.data?.domains ?? [];

  const record = useQuery({ queryKey: queryKeys.dataset(row.id), queryFn: fetchDataset });

  // The record's fields land once; over an edit in progress only the still-empty
  // record-only fields are filled (`foldRecord`).
  useEffect(() => {
    const data = record.data;
    if (!data) return;
    setForm((f) => foldRecord(f, data, touched));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.data]);

  useEffect(() => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    overlayRef.current?.querySelector<HTMLElement>(TABBABLE)?.focus();
    return () => {
      if (openerRef.current?.isConnected) openerRef.current.focus();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const trapTab = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const stops = Array.from(overlayRef.current?.querySelectorAll<HTMLElement>(TABBABLE) ?? []);
    const first = stops[0];
    const last = stops[stops.length - 1];
    if (!first || !last) return;
    if (document.activeElement !== (e.shiftKey ? first : last)) return;
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  }, []);

  const change = (patch: Partial<EditForm>) => {
    setTouched(true);
    setForm((f) => ({ ...f, ...patch }));
  };

  const save = async () => {
    setBusy(true);
    try {
      await patchDataset(row.id, patchBody(form, record.data !== undefined));
      onSaved?.();
      toast(t('manager.ds.saved', { title: form.title }));
      onClose();
    } catch (err) {
      // The dialog stays open with the edits intact — a failed save is a retry.
      const message = err instanceof ApiError || err instanceof Error ? err.message : '';
      toast(t('manager.ds.saveFailed', { message }));
      setBusy(false);
    }
  };

  return (
    <div className="modal-back show" onKeyDown={trapTab}>
      <div
        className="modal mgr-edit"
        ref={overlayRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mgrEditTitle"
        data-testid="mgr-edit"
      >
        <header>
          <div className="mgr-review-head">
            <h3 id="mgrEditTitle">{t('manager.ds.editTitle')}</h3>
            <p className="mgr-review-file">{row.id}</p>
          </div>
          <button type="button" className="close" aria-label={t('manager.ds.cancel')} onClick={onClose}>
            <Icon name="x" />
          </button>
        </header>

        <div className="mbody">
          <DatasetMetadataForm
            form={form}
            domains={domains}
            onChange={change}
            idPrefix="mgr-edit"
          />
        </div>

        <footer>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('manager.ds.cancel')}
          </button>
          <button
            type="button"
            className="btn primary"
            data-testid="mgr-edit-save"
            disabled={busy || form.title.trim() === ''}
            onClick={() => void save()}
          >
            {busy ? t('manager.ds.saving') : t('manager.ds.save')}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default EditDatasetModal;
